import {
  BadgesGroupedByOwner,
  BadgeType,
  BadgeTypeItemType,
  BadgeTypeType,
  CommercialBadge,
  PaginatedResponse,
  QueryArgs,
  SimpleUserBadge,
} from "@ac-expo/api-types";
import { privateAxios } from "../axiosInstances";
import { useQuery } from "@tanstack/react-query";
import badgeTypesQueryKeys, {
  badgesQueryKeys,
  EventBadgesFilters,
} from "../query-keys/badges.query-keys";
import { isAxiosError } from "axios";

type GetEventBadgesAssignedReturn = PaginatedResponse<BadgesGroupedByOwner>;

export const getEventBadgesAssigned = async (
  eventId: string,
  filters?: EventBadgesFilters
) => {
  try {
    const res = await privateAxios.get<GetEventBadgesAssignedReturn>(
      `/events/${eventId}/badges`,
      {
        params: filters,
      }
    );
    return res.data;
  } catch (error) {
    if (isAxiosError(error)) {
      error.message = error.response?.data.message ?? error.message;
    }
    throw error;
  }
};

export const useGetBadgesAssignedQuery = (
  eventId: string,
  filters?: EventBadgesFilters,
  args?: QueryArgs<GetEventBadgesAssignedReturn>
) => {
  return useQuery({
    queryKey: badgesQueryKeys.event(eventId, undefined, filters),
    queryFn: () => getEventBadgesAssigned(eventId, filters),
    ...args,
  });
};

const getEventBadgesTypes = async (eventId: string) => {
  const res = await privateAxios.get<BadgeTypeItemType[]>(
    `/events/${eventId}/badges/types`
  );
  return res.data;
};

export const useGetBadgeTypesEventsQuery = <TData = BadgeTypeItemType[]>(
  eventId: string,
  args?: QueryArgs<BadgeTypeItemType[], TData>
) => {
  return useQuery({
    queryKey: badgeTypesQueryKeys.event(eventId),
    queryFn: () => getEventBadgesTypes(eventId),
    ...args,
  });
};

const getBadgeType = async (eventId: string, badgeTypeId: string) => {
  const res = await privateAxios.get<BadgeTypeType>(
    `/events/${eventId}/badges/types/${badgeTypeId}`
  );
  return res.data;
};

export const useGetBadgeTypeQuery = (
  eventId?: string,
  badgeTypeId?: string,
  args?: QueryArgs<BadgeTypeType>
) => {
  return useQuery({
    queryKey: badgeTypesQueryKeys.get(eventId ?? "", badgeTypeId ?? ""),
    queryFn: () => getBadgeType(eventId ?? "", badgeTypeId ?? ""),
    enabled: !!eventId && !!badgeTypeId,
    ...args,
  });
};

const getExhibitorBadges = async (eventId: string, exhibitorId: string) => {
  const res = await privateAxios.get<BadgeType[]>(
    `/events/${eventId}/badges/exhibitors/${exhibitorId}`
  );
  return res.data;
};

export const useGetExhibitorBadgesQuery = (
  eventId: string,
  exhibitorId: string,
  args?: QueryArgs<BadgeType[]>
) => {
  return useQuery({
    queryKey: badgesQueryKeys.event(eventId, exhibitorId),
    queryFn: () => getExhibitorBadges(eventId, exhibitorId),
    ...args,
  });
};

const getUserBadges = async () => {
  const res = await privateAxios.get<SimpleUserBadge[]>(`/users/badges`);
  return res.data;
};

export const useGetUserBadgesQuery = (args?: QueryArgs<SimpleUserBadge[]>) => {
  return useQuery({
    queryKey: [],
    queryFn: () => getUserBadges(),
    ...args,
  });
};

const getCommercialBadges = async (eventId: string) => {
  const res = await privateAxios.get<CommercialBadge[]>(
    `/events/${eventId}/badges/commercial/me`
  );
  return res.data;
};

export const useGetCommercialBadgesQuery = (eventId: string) => {
  return useQuery({
    queryKey: badgesQueryKeys.commercial(eventId),
    queryFn: () => getCommercialBadges(eventId),
    enabled: !!eventId,
  });
};
